import { useEffect, useState, useRef } from "react";
import { ShareExpiredPage, isExpiredError } from "@/components/ShareExpiry";
import { useParams } from "react-router-dom";
import axios from "axios";
import { daDate, holidayInfo, holidayLabel, holidaySegments, barPieces } from "@/lib/date";
import { Download, FileText, FileSpreadsheet, FileImage, PenTool, File, Paperclip, X, CalendarDays, Building2, CircleDot, CheckCircle2, Clock, AlertTriangle, LayoutGrid, Activity, Layers, CalendarRange, Wallet, MessageSquare, Info, NotebookText } from "lucide-react";

const DAY = 86400000;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtUpdated = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("da-DK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Copenhagen" }).formatToParts(d);
  const g = (t) => parts.find((p) => p.type === t)?.value || "";
  return `${g("day")}-${g("month")}-${g("year")} kl. ${g("hour")}:${g("minute")}`;
};
const today = () => new Date().toISOString().slice(0, 10);
const MF = ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];
const isoWeek = (d) => { const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())); const day = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - day); const ys = new Date(Date.UTC(t.getUTCFullYear(), 0, 1)); return Math.ceil((((t - ys) / 86400000) + 1) / 7); };
function buildTicks(min, max, trackW) {
  const days = (max - min) / DAY + 1, span = (max - min) || 1;
  const fd = (ms) => { const d = new Date(ms); return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
  const mon = (ms) => { const d = new Date(ms); const wd = d.getDay() || 7; d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + (wd === 1 ? 0 : 8 - wd)); return d.getTime(); };
  let raw = [];
  if (days <= 31) {
    for (let t = mon(min); t <= max; t += 7 * DAY) raw.push([t, `Uge ${isoWeek(new Date(t))}`]);
    if (raw.length < 2) { raw = []; const step = Math.max(1, Math.round(days / 4)); for (let i = 0; i < days; i += step) raw.push([min + i * DAY, fd(min + i * DAY)]); }
  } else if (days <= 120) {
    for (let t = mon(min); t <= max; t += 7 * DAY) raw.push([t, `Uge ${isoWeek(new Date(t))}`]);
    let d = new Date(min); d.setDate(1); if (d.getTime() < min) d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    while (d.getTime() <= max) { raw.push([d.getTime(), MF[d.getMonth()]]); d = new Date(d.getFullYear(), d.getMonth() + 1, 1); }
  } else if (days <= 365) {
    raw.push([min, `${MF[new Date(min).getMonth()]} ${new Date(min).getFullYear()}`]);
    let d = new Date(new Date(min).getFullYear(), new Date(min).getMonth() + 1, 1);
    while (d.getTime() <= max) { raw.push([d.getTime(), `${MF[d.getMonth()]} ${d.getFullYear()}`]); d = new Date(d.getFullYear(), d.getMonth() + 1, 1); }
  } else {
    const q0 = new Date(min); raw.push([min, `Q${Math.floor(q0.getMonth() / 3) + 1} ${q0.getFullYear()}`]);
    let d = new Date(q0.getFullYear(), Math.floor(q0.getMonth() / 3) * 3 + 3, 1);
    while (d.getTime() <= max) { raw.push([d.getTime(), `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`]); d = new Date(d.getFullYear(), d.getMonth() + 3, 1); }
  }
  raw.sort((a, b) => a[0] - b[0]);
  const seen = new Set(); raw = raw.filter(([, l]) => (seen.has(l) ? false : (seen.add(l), true)));
  const items = raw.map(([ms, l]) => ({ pos: Math.min(1, Math.max(0, (ms - min) / span)), label: l }));
  if (items.length <= 2) return items;
  const maxlen = Math.max(...items.map((i) => i.label.length));
  const gapPx = maxlen * 6.6 + 14;
  const minGap = trackW > 0 ? gapPx / trackW : 0.12;
  const out = [items[0]];
  for (let i = 1; i < items.length - 1; i++) {
    if (items[i].pos - out[out.length - 1].pos >= minGap) out.push(items[i]);
  }
  const last = items[items.length - 1];
  while (out.length > 1 && last.pos - out[out.length - 1].pos < minGap) out.pop();
  out.push(last);
  return out;
}
const fmtSize = (n) => !n ? "" : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`;
const statusDa = { ikke_igangsat: "Ikke igangsat", aktiv: "Aktiv", paused: "Pauset", afsluttet: "Afsluttet" };

const ST = {
  "Ikke startet": { bg: "#F3F1EC", tx: "#78716C", bd: "#E7E5E4", bar: "#A8A29E" },
  "I gang": { bg: "#F4EFEC", tx: "#806B61", bd: "#E6DDD6", bar: "#A08979" },
  "Afventer": { bg: "#F9F1D9", tx: "#8A6E17", bd: "#F9F1D9", bar: "#C9A83F" },
  "Færdig": { bg: "#E7EADF", tx: "#7E856C", bd: "#D3D8C6", bar: "#7E856C" },
  "Forsinket": { bg: "#F1DEDA", tx: "#A83232", bd: "#E9C9C3", bar: "#A83232" },
  "Ikke igangsat": { bg: "#F3F1EC", tx: "#78716C", bd: "#E7E5E4", bar: "#A8A29E" },
  "Aktiv": { bg: "#E7EADF", tx: "#7E856C", bd: "#D3D8C6", bar: "#7E856C" },
  "Pauset": { bg: "#F4EFEC", tx: "#806B61", bd: "#F4EFEC", bar: "#A08979" },
  "Afsluttet": { bg: "#F3F1EC", tx: "#78716C", bd: "#E7E5E4", bar: "#A8A29E" },
};
const Badge = ({ status }) => {
  const s = ST[status] || ST["Ikke startet"];
  return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium" style={{ background: s.bg, color: s.tx, border: `1px solid ${s.bd}` }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: s.bar }} />{status}</span>;
};

const STATUS_OPTS = ["Ikke startet", "I gang", "Afventer", "Færdig"];
function StatusPicker({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const s = ST[value] || ST["Ikke startet"];
  return (
    <div className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-all hover:shadow-sm"
        style={{ background: s.bg, color: s.tx, border: `1px solid ${s.bd}` }} data-testid={`${testid}-trigger`}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.bar }} />{value}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-44 rounded-xl bg-white p-1.5 shadow-xl" style={{ border: "1px solid #EBE8E3" }}>
          {STATUS_OPTS.map((opt) => {
            const o = ST[opt];
            return (
              <button key={opt} type="button" onMouseDown={(e) => { e.preventDefault(); setOpen(false); if (opt !== value) onChange(opt); }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium text-left hover:bg-[#F3F1EC] transition-colors"
                style={{ color: o.tx }} data-testid={`${testid}-opt-${opt}`}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: o.bar }} />{opt}
                {opt === value && <span className="ml-auto" style={{ color: "#A83232" }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const fileIcon = (ext) => {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return FileText;
  if (["xls", "xlsx", "csv"].includes(e)) return FileSpreadsheet;
  if (["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(e)) return FileImage;
  if (["dwg", "dxf", "step", "stp", "iges"].includes(e)) return PenTool;
  return File;
};

const PUBLIC_HOLIDAY_HATCH = "repeating-linear-gradient(45deg, rgba(201,168,63,0.16), rgba(201,168,63,0.16) 6px, transparent 6px, transparent 12px)";

function Timeline({ steps }) {
  const wrapRef = useRef(null);
  const [w, setW] = useState(880);
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((e) => setW(e[0].contentRect.width));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);
  if (!steps.length) return null;
  const LABEL_W = 176;
  const dated = steps.filter((s) => s.start_date && s.expected_end_date);
  const min = dated.length ? Math.min(...dated.map((s) => new Date(s.start_date).getTime())) : null;
  const max = dated.length ? Math.max(...dated.map((s) => new Date(s.expected_end_date).getTime())) : null;
  const t0 = new Date(today()).getTime();
  const trackW = Math.max(120, w - LABEL_W);
  const ticks = min != null ? buildTicks(min, max, trackW) : [];
  const span = (max - min) || 1;
  const pct = (ms) => `${((ms - min) / span) * 100}%`;
  const todayIn = min != null && t0 >= min && t0 <= max;
  const DAY = 86400000;
  const shades = min != null ? holidaySegments(min, max) : [];
  return (
    <div ref={wrapRef} className="overflow-hidden" data-testid="public-timeline">
      {min != null && (
        <div className="flex justify-between text-sm mb-4" style={{ marginLeft: LABEL_W }}>
          <span style={{ color: "#78716C" }} data-testid="timeline-start">Start: <strong style={{ color: "#1A1919" }}>{daDate(new Date(min).toISOString())}</strong></span>
          <span style={{ color: "#78716C" }} data-testid="timeline-end">Slut: <strong style={{ color: "#1A1919" }}>{daDate(new Date(max).toISOString())}</strong></span>
        </div>
      )}
      {min != null && (
        <div className="relative h-5 mb-2" style={{ marginLeft: LABEL_W }}>
          {ticks.map((t, i) => (
            <span key={i} className="absolute text-[11px] font-medium whitespace-nowrap" style={{ left: `${t.pos * 100}%`, transform: t.pos > 0.92 ? "translateX(-100%)" : (t.pos < 0.02 ? "none" : "translateX(-50%)"), color: "#A8A29E" }}>{t.label}</span>
          ))}
        </div>
      )}
      <div className="relative">
        {shades.map((sh, i) => (
          <div key={`hs${i}`} className="absolute top-0 bottom-0 pointer-events-none flex items-start justify-center overflow-hidden" style={{ left: `${LABEL_W + ((sh.offset * DAY) / span) * trackW}px`, width: `${((sh.len * DAY) / span) * trackW}px`, background: PUBLIC_HOLIDAY_HATCH }}>
            <span className="mt-0.5 text-[10px] font-semibold whitespace-nowrap px-1" style={{ color: "#806B61" }}>{sh.label}</span>
          </div>
        ))}
        {todayIn && (
          <div className="absolute top-0 bottom-0 z-10 border-l border-dashed" style={{ left: `${LABEL_W + ((t0 - min) / span) * trackW}px`, borderColor: "#A83232" }}>
            <span className="absolute -top-4 -translate-x-1/2 text-[10px] font-semibold whitespace-nowrap" style={{ color: "#A83232" }}>I dag</span>
          </div>
        )}
        {steps.map((s, i) => {
          const has = s.start_date && s.expected_end_date;
          const st = s.delayed ? ST["Forsinket"] : (ST[s.status] || ST["Ikke startet"]);
          return (
            <div key={i} className="flex items-center h-11">
              <div className="shrink-0 pr-4 text-[13px] truncate" style={{ width: LABEL_W, color: "#1A1919" }} title={s.name}>{s.number}. {s.name}</div>
              <div className="relative flex-1 h-full">
                {has ? (
                  barPieces(new Date(s.start_date).getTime(), new Date(s.expected_end_date).getTime(), min).map((p, pi) => (
                    <div key={pi} className="absolute top-1/2 -translate-y-1/2 h-2.5 rounded-full" style={{ left: `${((p.offset * DAY) / span) * 100}%`, width: `${((p.len * DAY) / span) * 100}%`, background: st.bar }} title={`${daDate(s.start_date)} – ${daDate(s.expected_end_date)}`} />
                  ))
                ) : (
                  <span className="absolute top-1/2 -translate-y-1/2 text-xs italic" style={{ color: "#A8A29E" }}>Dato mangler</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Section = ({ title, children, id }) => (
  <section id={id} className="mt-16 md:mt-24 scroll-mt-24" data-testid={`section-${id}`}>
    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6" style={{ color: "#1A1919" }}>{title}</h2>
    {children}
  </section>
);
const Card = ({ children, className = "", style }) => (
  <div className={`bg-white rounded-2xl border p-6 md:p-8 ${className}`} style={{ borderColor: "#EBE8E3", boxShadow: "0 4px 24px rgba(0,0,0,0.03)", ...style }}>{children}</div>
);
const Meta = ({ label, value }) => (
  <div><p className="text-xs font-semibold uppercase tracking-[0.1em]" style={{ color: "#A8A29E" }}>{label}</p><p className="mt-1.5 text-base font-medium" style={{ color: "#1A1919" }}>{value}</p></div>
);
const initials = (name) => (name || "").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const InfoField = ({ label, value, children, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-[0.1em]" style={{ color: "#A8A29E" }}>{label}</p>
    {children || <p className="mt-1.5 text-base font-medium break-words [overflow-wrap:anywhere]" style={{ color: "#1A1919" }}>{value}</p>}
  </div>
);
function PmAvatar({ url, name }) {
  const cls = "h-16 w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 rounded-full shrink-0";
  const [err, setErr] = useState(false);
  return url && !err ? (
    <img src={url} alt={name || "Projektansvarlig"} data-testid="public-pm-avatar" className={`${cls} object-cover`} style={{ border: "1px solid #EBE8E3" }} onError={() => setErr(true)} />
  ) : (
    <div data-testid="public-pm-avatar-fallback" className={`${cls} flex items-center justify-center font-semibold`} style={{ background: "#F1DEDA", color: "#A83232", border: "1px solid #E9C9C3", fontSize: 22 }}>{initials(name) || "?"}</div>
  );
}
function StepAvatar({ url, name }) {
  const cls = "h-9 w-9 rounded-full shrink-0";
  const has = !!name;
  const [err, setErr] = useState(false);
  return url && !err ? (
    <img src={url} alt={name || "Ansvarlig"} className={`${cls} object-cover`} style={{ border: "1px solid #EBE8E3" }} onError={() => setErr(true)} />
  ) : (
    <div className={`${cls} flex items-center justify-center text-xs font-semibold`} style={{ background: has ? "#F1DEDA" : "#F5F5F4", color: has ? "#A83232" : "#A8A29E", border: `1px solid ${has ? "#E9C9C3" : "#E7E5E4"}` }}>{has ? initials(name) : "–"}</div>
  );
}

import { PrintPageStyle } from "@/components/print/PrintFrontMatter";
import { ProjectPrintDoc } from "@/components/print/ProjectPrint";
import { PrintButton } from "@/components/print/PrintButton";

export default function PublicProject() {
  const { token: tok0, pid: scopedPid } = useParams();
  const token = scopedPid ? `ps_${tok0}_${scopedPid}` : tok0;
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState("top");
  const [comments, setComments] = useState([]);
  const [minutes, setMinutes] = useState([]);
  const [cName, setCName] = useState("");
  const [cMsg, setCMsg] = useState("");
  const [cFiles, setCFiles] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const m1 = document.createElement("meta"); m1.name = "referrer"; m1.content = "no-referrer"; const m2 = document.createElement("meta"); m2.name = "robots"; m2.content = "noindex, nofollow"; document.head.append(m1, m2);
    return () => { m1.remove(); m2.remove(); };
  }, []);
  useEffect(() => {
    document.title = "BSM A/S — Projektpræsentation";
    const id = "gf-manrope";
    if (!document.getElementById(id)) {
      const l = document.createElement("link"); l.id = id; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap";
      document.head.appendChild(l);
    }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/projects/${token}`)
      .then((r) => setP(r.data)).catch((e) => setError(isExpiredError(e) ? "expired" : true));
  }, [token]);

  useEffect(() => {
    if (!p) return;
    const ids = ["top", "progress", "current", "phases", "timeline", "deadlines", "economy", "minutes", "documents", "comments"];
    const onScroll = () => {
      let current = "top";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) { for (let i = ids.length - 1; i >= 0; i--) { if (document.getElementById(ids[i])) { current = ids[i]; break; } } }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [p]);

  useEffect(() => {
    if (!p) return;
    if (p.share?.comments) axios.get(`${BACKEND}/api/public/projects/${token}/comments`).then((r) => setComments(r.data)).catch(() => {});
    if (p.share?.minutes) axios.get(`${BACKEND}/api/public/projects/${token}/minutes`).then((r) => setMinutes(r.data)).catch(() => {});
  }, [p, token]);

  const font = { fontFamily: "Manrope, Inter, ui-sans-serif, system-ui, sans-serif" };

  if (error === "expired") return <ShareExpiredPage />;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: "#F3F1EC", ...font }} data-testid="public-not-found">
      <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#A83232" }}>BSM A/S</p>
      <h1 className="text-3xl font-light mt-4" style={{ color: "#1A1919" }}>Dette projektlink er ikke længere aktivt.</h1>
      <p className="mt-3 max-w-md" style={{ color: "#A8A29E" }}>Kontakt din projektansvarlige hos BSM A/S for at få et opdateret link.</p>
    </div>
  );
  if (!p) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBFAF8", color: "#A8A29E", ...font }}>Indlæser projekt…</div>;

  const share = p.share || {};
  const steps = p.steps || [];
  const milestones = p.milestones || [];
  const projectFiles = p.project_files || [];
  const stepFiles = steps.flatMap((s) => s.files || []);
  const allDocs = [...projectFiles, ...stepFiles];
  const cur = p.current_step;
  const closed = p.status === "afsluttet";
  const ro = closed || !!p.readonly;
  const dlUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/public/projects/${token}/files/${fid}/download`;
  const renderDoc = (f) => {
    const Icon = fileIcon(f.ext);
    return (
      <div key={f.id} className="bg-white rounded-2xl border flex items-center gap-4 px-5 py-4" style={{ borderColor: "#EBE8E3", boxShadow: "0 4px 24px rgba(0,0,0,0.03)" }} data-testid={`public-doc-${f.id}`}>
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#F3F1EC" }}><Icon className="h-5 w-5" strokeWidth={1.5} style={{ color: "#78716C" }} /></div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate" style={{ color: "#1A1919" }}>{f.name}</p>
          <p className="text-sm" style={{ color: "#A8A29E" }}>{(f.ext || "fil").toUpperCase()}{f.size ? ` · ${fmtSize(f.size)}` : ""}{f.uploaded_at ? ` · Uploadet ${daDate(f.uploaded_at)}` : ""}</p>
          {f.description && <p className="text-sm mt-0.5" style={{ color: "#78716C" }}>{f.description}</p>}
        </div>
        <a href={dlUrl(f.id)} className="inline-flex items-center gap-1.5 text-sm font-medium rounded-full border px-4 py-2 transition-colors hover:bg-[#F3F1EC] shrink-0" style={{ borderColor: "#D6D3D1", color: "#1A1919" }} data-testid={`public-doc-download-${f.id}`}><Download className="h-4 w-4" strokeWidth={1.5} /> Hent</a>
      </div>
    );
  };
  const period = p.expected_end_date ? `${daDate(p.start_date)} – ${daDate(p.expected_end_date)}` : "Ikke fastlagt";
  const go = (id) => { const el = document.getElementById(id); if (el) { setActive(id); el.scrollIntoView({ behavior: "smooth", block: "start" }); } };
  const loadComments = () => axios.get(`${BACKEND}/api/public/projects/${token}/comments`).then((r) => setComments(r.data)).catch(() => {});
  const setStepStatus = async (stepId, status) => {
    try {
      await axios.patch(`${BACKEND}/api/public/projects/${token}/steps/${stepId}/status`, { status });
      const r = await axios.get(`${BACKEND}/api/public/projects/${token}`);
      setP(r.data);
    } catch (_) { /* noop */ }
  };
  const submitComment = async () => {
    const m = cMsg.trim(); if (!m && cFiles.length === 0) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("message", m);
      fd.append("author_name", cName.trim());
      cFiles.forEach((f) => fd.append("files", f));
      await axios.post(`${BACKEND}/api/public/projects/${token}/comments`, fd);
      setCMsg(""); setCFiles([]); await loadComments();
    }
    catch (e) { /* noop */ }
    finally { setSending(false); }
  };
  const navItems = [
    { id: "top", label: "Overblik", icon: LayoutGrid },
    ...(share.progress ? [{ id: "progress", label: "Projektfremdrift", icon: Activity }] : []),
    ...(cur ? [{ id: "current", label: "Aktuelt trin", icon: CircleDot }] : []),
    ...(share.phases && steps.length ? [{ id: "phases", label: "Projektfaser", icon: Layers }, { id: "timeline", label: "Tidsplan", icon: CalendarRange }] : []),
    ...(share.deadlines && milestones.length ? [{ id: "deadlines", label: "Deadlines", icon: CalendarDays }] : []),
    ...(share.economy && p.economy && (p.economy.lines || []).length ? [{ id: "economy", label: "Økonomi", icon: Wallet }] : []),
    ...(share.documents && allDocs.length ? [{ id: "documents", label: "Dokumenter", icon: FileText }] : []),
    ...(share.minutes && minutes.length ? [{ id: "minutes", label: "Mødereferat", icon: NotebookText }] : []),
    ...(share.comments ? [{ id: "comments", label: "Kommunikation", icon: MessageSquare }] : []),
  ];

  return (
    <div className="min-h-screen flex overflow-x-hidden pub-print-doc" style={{ background: "#FBFAF8", color: "#1A1919", ...font }} data-testid="public-project">
      <PrintPageStyle title={p.name} date={p.last_updated ? daDate(p.last_updated) : ""} />
      <ProjectPrintDoc p={p} share={share} steps={steps} milestones={milestones} minutes={minutes} comments={comments} projectFiles={projectFiles} navItems={navItems} />
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 fixed inset-y-0 bg-white border-r z-30" style={{ borderColor: "#EBE8E3" }} data-testid="public-sidebar">
        <div className="flex items-center px-6 h-20 shrink-0">
          <img src="/bsm-logo.png" alt="BSM A/S — vision in steel" data-testid="public-brand-logo" className="h-9 w-auto" />
        </div>
        <div className="px-6 pb-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#A8A29E" }}>Projekt for</p>
          <p className="text-sm font-semibold mt-1 truncate" style={{ color: "#1A1919" }}>{p.customer_name || p.name}</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {navItems.map((n) => {
            const A = active === n.id;
            return (
              <button key={n.id} onClick={() => go(n.id)} data-testid={`public-nav-${n.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-colors ${A ? "bg-[#F1DEDA] text-[#A83232]" : "text-stone-600 hover:bg-[#F3F1EC] hover:text-stone-900"}`}>
                <n.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="border-t p-5 text-xs" style={{ borderColor: "#EBE8E3", color: "#A8A29E" }}>BSM A/S · {new Date().getFullYear()}</div>
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
        {/* Mobil top-bar + nav */}
        <div className="lg:hidden sticky top-0 z-20 bg-white/90 backdrop-blur border-b" style={{ borderColor: "#EBE8E3" }}>
          <div className="flex items-center justify-between px-5 h-16">
            <img src="/bsm-logo.png" alt="BSM A/S — vision in steel" className="h-7 w-auto" />
            {p.last_updated && <p className="text-[11px]" style={{ color: "#A8A29E" }}>Opdateret {fmtUpdated(p.last_updated)}</p>}
          </div>
          <div className="flex gap-2 overflow-x-auto px-5 pb-3">
            {navItems.map((n) => (
              <button key={n.id} onClick={() => go(n.id)} className={`whitespace-nowrap text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${active === n.id ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : "text-stone-600 border-[#EBE8E3]"}`}>{n.label}</button>
            ))}
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-24">
          {/* Hero */}
          <div id="top" className="scroll-mt-24 flex items-center justify-end gap-4 min-h-[1rem]">
            <PrintButton filename={`Projekt ${p.name}`} />
            {p.last_updated && <p className="hidden lg:block text-xs" data-testid="public-last-updated" style={{ color: "#A8A29E" }}>Sidst opdateret {fmtUpdated(p.last_updated)}</p>}
          </div>

          {closed && (
            <div className="mt-8 flex items-start gap-3 rounded-2xl border px-5 py-4" style={{ borderColor: "#D3D8C6", background: "#E7EADF" }} data-testid="public-closed-banner">
              <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" strokeWidth={1.75} style={{ color: "#7E856C" }} />
              <div className="min-w-0">
                <p className="text-sm font-semibold" style={{ color: "#1A1919" }}>Projektet er afsluttet ✓</p>
                <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>Siden er nu skrivebeskyttet. Du kan fortsat se alt indhold og hente dokumenter.</p>
                {(p.closed_at || p.last_updated) && <p className="text-xs mt-1.5 font-medium" data-testid="public-closed-date" style={{ color: "#7E856C" }}>Afsluttet den {daDate(p.closed_at || p.last_updated)}</p>}
              </div>
            </div>
          )}

        <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-x-10 gap-y-8 lg:gap-y-10 items-start">
          {/* Titel + beskrivelse */}
          <div className="min-w-0 order-1">
            <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "#A8A29E" }}>Projektoversigt</p>
            <h1 className="mt-3 text-4xl md:text-5xl font-light tracking-tight break-words [overflow-wrap:anywhere]" data-testid="public-project-title" style={{ color: "#1A1919" }}>{p.name}</h1>
            {share.description && p.description && <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "#78716C" }}>{p.description}</p>}
          </div>

          {/* Projektansvarlig */}
          {share.contact ? (
            <div className="w-full bg-white rounded-2xl border p-5 md:p-6 order-3 lg:order-2" style={{ borderColor: "#EBE8E3", boxShadow: "0 4px 24px rgba(0,0,0,0.03)" }} data-testid="public-pm-card">
              <p className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: "#A83232" }}>Projektansvarlig</p>
              <div className="mt-4 flex items-center gap-4">
                <PmAvatar url={p.project_manager_avatar ? `${BACKEND}${p.project_manager_avatar}` : null} name={p.project_manager_name} />
                <div className="min-w-0">
                  <p className="text-base font-semibold break-words [overflow-wrap:anywhere]" data-testid="public-pm-name" style={{ color: "#1A1919" }}>{p.project_manager_name || "—"}</p>
                  {p.project_manager_title && <p className="text-sm mt-0.5" style={{ color: "#78716C" }}>{p.project_manager_title}</p>}
                </div>
              </div>
              {(p.project_manager_phone || p.project_manager_email) && (
                <div className="mt-4 pt-4 space-y-1.5 text-sm border-t" style={{ borderColor: "#F3F1EC", color: "#78716C" }}>
                  {p.project_manager_phone && <p><span style={{ color: "#A8A29E" }}>Telefon: </span><a href={`tel:${p.project_manager_phone.replace(/\s/g, "")}`} className="hover:underline" style={{ color: "#1A1919" }} data-testid="public-pm-phone">{p.project_manager_phone}</a></p>}
                  {p.project_manager_email && <p className="break-all"><span style={{ color: "#A8A29E" }}>E-mail: </span><a href={`mailto:${p.project_manager_email}`} className="hover:underline font-medium" style={{ color: "#A83232" }} data-testid="public-pm-email">{p.project_manager_email}</a></p>}
                </div>
              )}
            </div>
          ) : <div className="hidden lg:block order-2" />}

          {/* Kunde / Periode / Status */}
          <div className="min-w-0 order-2 lg:order-3 grid grid-cols-2 sm:grid-cols-3 gap-x-10 gap-y-7" data-testid="public-info-line">
            <InfoField label="Kunde" value={p.customer_name || "Ikke angivet"} testid="public-info-customer" />
            <InfoField label="Projektperiode" testid="public-info-period">
              <p className="mt-1.5 text-base font-medium" style={{ color: "#1A1919" }}>
                {p.expected_end_date ? (
                  <><span className="whitespace-nowrap">{daDate(p.start_date)}</span> – <span className="whitespace-nowrap">{daDate(p.expected_end_date)}</span></>
                ) : "Ikke fastlagt"}
              </p>
            </InfoField>
            <InfoField label="Status" testid="public-info-status"><div className="mt-1.5"><Badge status={statusDa[p.status] || p.status} /></div></InfoField>
          </div>

          {/* Kontaktperson */}
          {share.contact && p.customer_contact && (p.customer_contact.name || p.customer_contact.email || p.customer_contact.phone) && (
            <div className="w-full bg-white rounded-2xl border p-5 md:p-6 order-4" style={{ borderColor: "#EBE8E3", boxShadow: "0 4px 24px rgba(0,0,0,0.03)" }} data-testid="public-contact-card">
              <p className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: "#A8A29E" }}>Kontaktperson</p>
              <p className="mt-3 text-base font-semibold break-words [overflow-wrap:anywhere]" data-testid="public-contact-name" style={{ color: "#1A1919" }}>{p.customer_contact.name || "—"}</p>
              {(p.customer_contact.phone || p.customer_contact.email) && (
                <div className="mt-3 pt-3 space-y-1.5 text-sm border-t" style={{ borderColor: "#F3F1EC", color: "#78716C" }}>
                  {p.customer_contact.phone && <p><span style={{ color: "#A8A29E" }}>Telefon: </span><a href={`tel:${p.customer_contact.phone.replace(/\s/g, "")}`} className="hover:underline" style={{ color: "#1A1919" }} data-testid="public-contact-phone">{p.customer_contact.phone}</a></p>}
                  {p.customer_contact.email && <p className="break-all"><span style={{ color: "#A8A29E" }}>E-mail: </span><a href={`mailto:${p.customer_contact.email}`} className="hover:underline font-medium" style={{ color: "#A83232" }} data-testid="public-contact-email">{p.customer_contact.email}</a></p>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fremdrift */}
        {share.progress && (
          <Section title="Projektfremdrift" id="progress">
            <Card>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="text-5xl font-light" style={{ color: "#1A1919" }}>{p.progress ?? 0}%</span>
                <span className="text-base" style={{ color: "#78716C" }}>{p.done_tasks ?? 0} af {p.total_tasks ?? 0} trin afsluttet</span>
              </div>
              <div className="mt-6 h-2.5 rounded-full overflow-hidden" style={{ background: "#ECEAE5" }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${p.progress ?? 0}%`, background: "#A83232" }} data-testid="progress-fill" />
              </div>
              <p className="mt-4 text-sm" style={{ color: "#A8A29E" }}>Projektperiode: {period}</p>
            </Card>
          </Section>
        )}

        {/* Aktuelt trin */}
        {cur && (
          <Section title="Aktuelt trin" id="current">
            <Card style={{ borderColor: "#1A1919", boxShadow: "0 12px 48px rgba(0,0,0,0.06)" }}>
              <div className="flex flex-wrap items-center gap-3">
                <CircleDot className="h-5 w-5" strokeWidth={1.5} style={{ color: "#A83232" }} />
                <h3 className="text-xl font-semibold" style={{ color: "#1A1919" }}>{cur.name}</h3>
                <Badge status={cur.status} />
              </div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-6">
                {share.responsibles && <Meta label="Ansvarlig for trinnet" value={cur.responsible || "Ikke tildelt"} />}
                <Meta label="Forventet afslutning" value={daDate(cur.expected_end_date)} />
                {share.contact && <Meta label="Projektansvarlig" value={p.project_manager_name || "—"} />}
              </div>
              {share.description && cur.description && <p className="mt-6 leading-relaxed" style={{ color: "#78716C" }}>{cur.description}</p>}
            </Card>
          </Section>
        )}

        {/* Faser */}
        {share.phases && steps.length > 0 && (
          <Section title="Projektfaser" id="phases">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {steps.map((s) => { const canEdit = s.editable_by_customer && !ro; return (
                <div key={s.number} data-testid={`phase-card-${s.number}`}
                  className="bg-white rounded-2xl p-6 transition-all relative"
                  style={{ border: canEdit ? "1.5px solid #A08979" : (s.is_current ? "1px solid #1A1919" : "1px solid #EBE8E3"), boxShadow: canEdit ? "0 8px 32px rgba(128,107,97,0.14)" : (s.is_current ? "0 12px 40px rgba(0,0,0,0.07)" : "0 4px 24px rgba(0,0,0,0.03)"), opacity: s.status === "Færdig" ? 0.92 : 1 }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.1em]" style={{ color: canEdit ? "#806B61" : "#A8A29E" }}>Trin {s.number}</span>
                    {canEdit
                      ? <StatusPicker value={s.status} onChange={(st) => setStepStatus(s.id, st)} testid={`phase-status-${s.number}`} />
                      : <Badge status={s.delayed ? "Forsinket" : s.status} />}
                  </div>
                  <h3 className="mt-3 text-lg font-semibold leading-snug" style={{ color: "#1A1919" }}>{s.name}</h3>
                  {canEdit && (
                    <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: "#F4EFEC", border: "1px solid #E6DDD6" }} data-testid={`phase-editable-hint-${s.number}`}>
                      <PenTool className="h-3.5 w-3.5 shrink-0" strokeWidth={2} style={{ color: "#806B61" }} />
                      <span className="text-xs font-semibold" style={{ color: "#806B61" }}>Kundens svar påkrævet – klik på status øverst for at opdatere</span>
                    </div>
                  )}
                  {(s.responsibles && s.responsibles.length > 0) ? (
                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2.5" data-testid={`phase-responsible-${s.number}`}>
                      {s.responsibles.map((r, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <StepAvatar url={r.avatar ? `${BACKEND}${r.avatar}` : null} name={r.name} />
                          <span className="text-sm font-medium" style={{ color: "#78716C" }}>{r.name || "Ikke tildelt"}</span>
                        </div>
                      ))}
                    </div>
                  ) : s.responsible !== undefined && (
                    <div className="mt-4 flex items-center gap-2.5" data-testid={`phase-responsible-${s.number}`}>
                      <StepAvatar url={s.responsible_avatar ? `${BACKEND}${s.responsible_avatar}` : null} name={s.responsible} />
                      <span className="text-sm font-medium" style={{ color: "#78716C" }}>{s.responsible || "Ikke tildelt"}</span>
                    </div>
                  )}
                  <p className="mt-2 text-sm flex items-center gap-1.5" style={{ color: "#78716C" }}><CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} />{daDate(s.start_date)} – {daDate(s.expected_end_date)}</p>
                  {s.status === "Færdig" && s.late_days > 0 && (
                    <p className="mt-1.5 text-xs flex items-center gap-1.5 font-semibold" style={{ color: "#806B61" }} data-testid={`phase-late-${s.number}`}><Clock className="h-3.5 w-3.5 shrink-0" strokeWidth={2} /> Afsluttet {s.late_days} {s.late_days === 1 ? "dag" : "dage"} for sent</p>
                  )}
                  {(() => { const hi = holidayInfo(s.start_date, s.expected_end_date); return hi ? (<p className="mt-1.5 text-xs flex items-start gap-1.5" style={{ color: "#806B61" }} data-testid={`phase-holiday-${s.number}`}><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={1.75} /> {holidayLabel(hi, { customer: true })}</p>) : null; })()}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs mb-1.5" style={{ color: "#A8A29E" }}><span>Fremdrift</span><span>{s.progress}%</span></div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#ECEAE5" }}><div className="h-full rounded-full" style={{ width: `${s.progress}%`, background: (ST[s.status] || ST["Ikke startet"]).bar }} /></div>
                  </div>
                </div>
              ); })}
            </div>
          </Section>
        )}

        {/* Tidslinje */}
        {share.phases && steps.length > 0 && (
          <Section title="Tidsplan" id="timeline">
            <Card>{p.expected_end_date ? <Timeline steps={steps} /> : <p className="text-sm" data-testid="timeline-not-set" style={{ color: "#A8A29E" }}>Tidsplanen er endnu ikke fastlagt.</p>}</Card>
          </Section>
        )}

        {/* Deadlines */}
        {share.deadlines && milestones.length > 0 && (
          <Section title="Deadlines" id="deadlines">
            {p.deadline_reason && (
              <div className="rounded-2xl border px-5 py-3 mb-3 text-sm" style={{ borderColor: "#E6DDD6", background: "#F4EFEC", color: "#78716C" }} data-testid="deadline-reason-banner">
                <span className="font-semibold" style={{ color: "#806B61" }}>Årsag til ændret deadline:</span> {p.deadline_reason}
              </div>
            )}
            {(p.deadline_history || []).length > 0 && (
              <div className="rounded-2xl border px-5 py-4 mb-3" style={{ borderColor: "#EBE8E3", background: "#fff" }} data-testid="deadline-history">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] mb-3" style={{ color: "#A8A29E" }}>Hvorfor er deadline rykket? <span className="normal-case tracking-normal font-normal">· nyeste øverst</span></p>
                <div>
                  {[...p.deadline_history].reverse().map((h, i) => (
                    <div key={i} className="grid grid-cols-[96px_1fr] gap-4 py-3.5" style={{ borderBottom: i < p.deadline_history.length - 1 ? "1px solid #F3F1EC" : "none" }} data-testid={`deadline-history-${i}`}>
                      <div>
                        <p className="text-[15px] font-bold whitespace-nowrap" style={{ color: "#806B61" }}>{daDate(h.to)}</p>
                        <p className="text-[11px] mt-0.5 whitespace-nowrap" style={{ color: "#A8A29E", textDecoration: "line-through" }}>{daDate(h.from)}</p>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {i === 0 && <span className="text-[10px] font-semibold uppercase tracking-[0.08em] rounded-full px-2 py-0.5" style={{ background: "#F4EFEC", color: "#806B61", border: "1px solid #E6DDD6" }} data-testid="deadline-history-latest">Nyeste</span>}
                          {h.reason && <p className="text-sm font-medium" style={{ color: "#1A1919" }}>{h.reason}</p>}
                        </div>
                        {(h.by || h.at) && <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>{h.by ? h.by : ""}{h.by && h.at ? " · " : ""}{h.at ? daDate(h.at) : ""}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-3">
              {milestones.map((m, i) => {
                const mEdit = m.editable_by_customer && !ro;
                const overdue = !m.done && (m.overdue || (m.days_until != null && m.days_until < 0));
                const soon = !m.done && !overdue && m.days_until != null && m.days_until <= 7;
                const note = m.done ? "Afsluttet" : (m.days_until == null ? "Ingen dato" : m.days_until < 0 ? `${Math.abs(m.days_until)} dage forsinket` : m.days_until === 0 ? "I dag" : `Om ${m.days_until} dage`);
                const accent = m.done ? "#7E856C" : overdue ? "#A83232" : soon ? "#8A6E17" : "#57534E";
                const bg = m.done ? "#E7EADF" : overdue ? "#F1DEDA" : soon ? "#F9F1D9" : "#F5F5F4";
                const Icon = m.done ? CheckCircle2 : overdue ? AlertTriangle : Clock;
                return (
                  <div key={i} className="flex items-center gap-4 rounded-2xl border px-5 py-4 transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_24px_rgba(0,0,0,0.05)]" style={{ borderColor: mEdit ? "#A08979" : "#EBE8E3", borderWidth: mEdit ? 1.5 : 1, background: mEdit ? "#F4EFEC" : "#fff" }} data-testid={`milestone-${i}`}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl shrink-0" style={{ background: mEdit ? "#F4EFEC" : bg }}>
                      <Icon className="h-5 w-5" strokeWidth={1.75} style={{ color: mEdit ? "#806B61" : accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" style={{ color: "#1A1919" }}>{m.name}</p>
                      <p className="text-sm mt-0.5" style={{ color: "#A8A29E" }}>
                        {m.moved && m.original_date ? (
                          <span data-testid={`milestone-moved-${i}`}>Oprindelig: <span style={{ textDecoration: "line-through" }}>{daDate(m.original_date)}</span> · Ny: <span style={{ color: "#806B61", fontWeight: 600 }}>{daDate(m.date)}</span></span>
                        ) : daDate(m.date)}
                        {mEdit && <span className="ml-2 font-semibold" style={{ color: "#806B61" }}>· Kundens svar påkrævet – opdater status →</span>}
                      </p>
                      {m.done && m.late_days > 0 && (
                        <p className="text-xs mt-1 flex items-center gap-1.5 font-semibold" style={{ color: "#806B61" }} data-testid={`milestone-late-${i}`}><Clock className="h-3 w-3 shrink-0" strokeWidth={2} /> Afsluttet {m.late_days} {m.late_days === 1 ? "dag" : "dage"} for sent</p>
                      )}
                    </div>
                    {mEdit
                      ? <StatusPicker value={m.status} onChange={(st) => setStepStatus(m.id, st)} testid={`milestone-status-${i}`} />
                      : <span className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap" style={{ background: bg, color: accent }}>{note}</span>}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Økonomi */}
        {share.economy && p.economy && (p.economy.lines || []).length > 0 && (() => {
          const e = p.economy;
          const fmtKr = (n) => new Intl.NumberFormat("da-DK").format(Math.round(n || 0)) + " kr";
          const fmtH = (n) => new Intl.NumberFormat("da-DK", { maximumFractionDigits: 2 }).format(n || 0) + " t";
          return (
            <Section title="Økonomi" id="economy">
              <Card className="!p-0 md:!p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: "1px solid #EBE8E3" }}>
                        <th className="text-left font-medium px-6 md:px-8 py-4" style={{ color: "#A8A29E" }}>Fase</th>
                        <th className="text-right font-medium px-4 py-4 whitespace-nowrap" style={{ color: "#A8A29E" }}>Kunde betalte timer</th>
                        <th className="text-right font-medium px-6 md:px-8 py-4 whitespace-nowrap" style={{ color: "#A8A29E" }}>Kunde økonomi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {e.lines.map((l, i) => (
                        <tr key={i} style={{ borderTop: i ? "1px solid #F3F1EC" : "none" }} data-testid={`public-economy-${i}`}>
                          <td className="px-6 md:px-8 py-3.5" style={{ color: "#1A1919" }}>{l.custom ? l.name : `${l.number}. ${l.name}`}</td>
                          <td className="text-right px-4 py-3.5 whitespace-nowrap" style={{ color: "#78716C" }}>{l.custom ? "—" : (l.budget_hours ? fmtH(l.budget_hours) : "—")}</td>
                          <td className="text-right px-6 md:px-8 py-3.5 font-medium whitespace-nowrap" style={{ color: "#1A1919" }}>{l.budget_kr ? fmtKr(l.budget_kr) : "—"}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: "1px solid #EBE8E3", background: "#FBFAF8" }} data-testid="public-economy-total">
                        <td className="px-6 md:px-8 py-4 font-semibold" style={{ color: "#1A1919" }}>I alt</td>
                        <td className="text-right px-4 py-4 font-semibold whitespace-nowrap" style={{ color: "#1A1919" }}>{fmtH(e.total_budget_hours)}</td>
                        <td className="text-right px-6 md:px-8 py-4 font-bold whitespace-nowrap" style={{ color: "#A83232" }}>{fmtKr(e.total_budget_kr)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
              <p className="mt-4 text-sm" style={{ color: "#A8A29E" }}>Budgettet er de timer og det beløb, der er aftalt for projektet.</p>
            </Section>
          );
        })()}

        {/* Mødereferat */}
        {share.minutes && minutes.length > 0 && (
          <Section title="Mødereferat" id="minutes">
            <div className="space-y-4">
              {minutes.map((m) => {
                return (
                  <Card key={m.id} data-testid={`public-minute-${m.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="text-lg font-semibold" style={{ color: "#1A1919" }}>{m.title}</h3>
                      <span className="text-sm inline-flex items-center gap-1.5" style={{ color: "#A8A29E" }}><CalendarDays className="h-3.5 w-3.5" /> {daDate(m.date)}</span>
                    </div>
                    {(m.bsm_attendees || []).length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.15em] mb-2" style={{ color: "#A8A29E" }}>Deltagere fra BSM</p>
                        <div className="flex flex-wrap gap-2">
                          {(m.bsm_attendees || []).map((u) => (
                            <span key={u.id} className="flex items-center gap-1.5 rounded-full pl-0.5 pr-2.5 py-0.5" style={{ background: "#F3F1EC", border: "1px solid #EBE8E3" }}>
                              {u.avatar_url
                                ? <img src={`${BACKEND}${u.avatar_url}`} alt={u.name} className="h-6 w-6 rounded-full object-cover" style={{ border: "1px solid #EBE8E3" }} onError={(e) => { e.currentTarget.outerHTML = `<span class="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold" style="background:#F1DEDA;color:#A83232;border:1px solid #E9C9C3">${(u.name || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase()}</span>`; }} />
                                : <span className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ background: "#F1DEDA", color: "#A83232", border: "1px solid #E9C9C3" }}>{(u.name || "").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("")}</span>}
                              <span className="text-xs font-medium" style={{ color: "#1A1919" }}>{u.name}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {m.customer_attendees && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.15em] mb-1" style={{ color: "#A8A29E" }}>Deltagere fra kunde</p>
                        <p className="text-sm" style={{ color: "#78716C" }}>{m.customer_attendees}</p>
                      </div>
                    )}
                    {m.notes && <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "#78716C" }}>{m.notes}</p>}
                    {(m.action_items || []).length > 0 && (
                      <div className="mt-4 pt-4" style={{ borderTop: "1px solid #F3F1EC" }}>
                        <p className="text-xs font-semibold uppercase tracking-[0.15em] mb-2.5" style={{ color: "#A8A29E" }}>Action-punkter</p>
                        <ul className="space-y-1.5 list-disc pl-5">
                          {m.action_items.map((a, i) => (
                            <li key={i} className="text-sm" style={{ color: "#1A1919" }} data-testid={`public-minute-${m.id}-action-${i}`}>{typeof a === "string" ? a : a.text}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </Section>
        )}

        {/* Dokumenter */}
        {share.documents && allDocs.length > 0 && (
          <Section title="Projektdokumenter" id="documents">
            <div className="space-y-8">
              {steps.filter((s) => (s.files || []).length > 0).map((s) => (
                <div key={s.number} data-testid={`doc-group-${s.number}`}>
                  <div className="flex items-center gap-3 mb-3 px-1 flex-wrap">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em]" style={{ color: "#A8A29E" }}>Trin {s.number}</span>
                    <span className="text-base font-semibold" style={{ color: "#1A1919" }}>{s.name}</span>
                    <Badge status={s.delayed ? "Forsinket" : s.status} />
                    <span className="ml-auto text-xs font-semibold rounded-full px-2.5 py-1" style={{ color: "#A8A29E", background: "#F3F1EC", border: "1px solid #EBE8E3" }}>{s.files.length} {s.files.length === 1 ? "fil" : "filer"}</span>
                  </div>
                  <div className="h-px mb-3" style={{ background: "linear-gradient(to right, #EBE8E3, transparent)" }} />
                  <div className="space-y-3">{s.files.map(renderDoc)}</div>
                </div>
              ))}
              {projectFiles.length > 0 && (
                <div data-testid="doc-group-other">
                  <div className="flex items-center gap-3 mb-3 px-1 flex-wrap">
                    <span className="text-base font-semibold" style={{ color: "#1A1919" }}>Øvrige projektdokumenter</span>
                    <span className="ml-auto text-xs font-semibold rounded-full px-2.5 py-1" style={{ color: "#A8A29E", background: "#F3F1EC", border: "1px solid #EBE8E3" }}>{projectFiles.length} {projectFiles.length === 1 ? "fil" : "filer"}</span>
                  </div>
                  <div className="h-px mb-3" style={{ background: "linear-gradient(to right, #EBE8E3, transparent)" }} />
                  <div className="space-y-3">{projectFiles.map(renderDoc)}</div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Kommunikation */}
        {share.comments && (
          <Section title="Kommunikation" id="comments">
            <Card className="!p-0 md:!p-0 overflow-hidden">
              <div className="px-6 md:px-8 py-6 space-y-5">
                {comments.length === 0 ? (
                  <div className="flex flex-col items-center text-center py-10">
                    <div className="h-14 w-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: "#F1DEDA" }}>
                      <MessageSquare className="h-6 w-6" strokeWidth={1.75} style={{ color: "#A83232" }} />
                    </div>
                    <p className="text-base font-semibold" style={{ color: "#1A1919" }}>Start en samtale med BSM</p>
                    <p className="text-sm mt-1.5 max-w-sm" style={{ color: "#A8A29E" }}>Skriv din første besked herunder — spørgsmål, kommentarer eller ønsker til projektet.</p>
                  </div>
                ) : (
                  comments.map((c) => {
                    const bsm = c.source === "bsm";
                    return (
                      <div key={c.id} className={`flex gap-3 ${bsm ? "" : "flex-row-reverse"}`} data-testid={`public-comment-${c.id}`}>
                        <div className="h-9 w-9 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0" style={{ background: bsm ? "#A83232" : "#E7E3DC", color: bsm ? "#fff" : "#78716C" }}>{bsm ? "BSM" : ((c.author_name || "").trim()[0] || "D").toUpperCase()}</div>
                        <div className={`max-w-[78%] flex flex-col ${bsm ? "items-start" : "items-end"}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold" style={{ color: "#1A1919" }}>{bsm ? "BSM A/S" : (c.author_name || "Dig")}</span>
                            <span className="text-[11px]" style={{ color: "#A8A29E" }}>{fmtUpdated(c.created_at)}</span>
                          </div>
                          {c.message && <div className="rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap" style={bsm ? { background: "#F4EFEC", color: "#5C4A42", borderTopLeftRadius: 5 } : { background: "#F1F3F5", color: "#1A1919", borderTopRightRadius: 5 }}>{c.message}</div>}
                          {(c.attachments || []).length > 0 && (
                            <div className="mt-1.5 flex flex-col gap-1.5 w-full" style={{ alignItems: bsm ? "flex-start" : "flex-end" }}>
                              {c.attachments.map((a) => (
                                <a key={a.id} href={`${BACKEND}/api/public/projects/${token}/comments/attachments/${a.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs rounded-xl px-3 py-2 transition-all hover:shadow-sm" style={{ background: "#fff", border: "1px solid #E6DDD6", color: "#5C4A42" }} data-testid={`public-comment-attachment-${a.id}`}>
                                  <FileText className="h-3.5 w-3.5 shrink-0" style={{ color: "#806B61" }} />
                                  <span className="max-w-[180px] truncate font-medium">{a.filename}</span>
                                  <Download className="h-3 w-3 shrink-0" style={{ color: "#A8A29E" }} />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {ro ? (
                <div className="border-t px-4 md:px-6 py-6 text-center" style={{ borderColor: "#EBE8E3", background: "#FBFAF8" }} data-testid="public-comment-closed">
                  <p className="text-sm" style={{ color: "#78716C" }}>{closed ? "Projektet er afsluttet — det er ikke længere muligt at skrive beskeder." : "Skrivebeskyttet visning — kontakt BSM direkte med spørgsmål."}</p>
                </div>
              ) : (
              <div className="border-t px-4 md:px-6 py-5" style={{ borderColor: "#EBE8E3", background: "#FBFAF8" }}>
                <div className="rounded-2xl border bg-white transition-colors focus-within:border-[#806B61]/50" style={{ borderColor: "#EBE8E3" }}>
                  <input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="Dit navn (valgfrit)" className="w-full px-4 pt-3 pb-1.5 text-sm bg-transparent focus:outline-none placeholder:text-[#A8A29E]" style={{ color: "#1A1919" }} data-testid="public-comment-name" />
                  <div className="h-px mx-4" style={{ background: "#F3F1EC" }} />
                  <textarea value={cMsg} onChange={(e) => setCMsg(e.target.value)} rows={3} placeholder="Skriv en besked til BSM…" className="w-full px-4 py-3 text-sm bg-transparent resize-none focus:outline-none placeholder:text-[#A8A29E]" style={{ color: "#1A1919" }} data-testid="public-comment-input" />
                  {cFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 px-3 pb-1" data-testid="public-comment-files">
                      {cFiles.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1" style={{ background: "#F4EFEC", color: "#5C4A42", border: "1px solid #E6DDD6" }}>
                          <File className="h-3 w-3 shrink-0" /> <span className="max-w-[140px] truncate">{f.name}</span>
                          <button onClick={() => setCFiles(cFiles.filter((_, j) => j !== i))} data-testid={`public-comment-file-remove-${i}`} className="ml-0.5"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between px-3 pb-3">
                    <label className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer rounded-full px-3 py-1.5 transition-colors hover:bg-[#F4EFEC]" style={{ color: "#806B61" }} data-testid="public-comment-attach">
                      <Paperclip className="h-3.5 w-3.5" /> Vedhæft fil
                      <input type="file" multiple className="hidden" onChange={(e) => { setCFiles([...cFiles, ...Array.from(e.target.files)]); e.target.value = ""; }} data-testid="public-comment-file-input" />
                    </label>
                    <button onClick={submitComment} disabled={sending || (!cMsg.trim() && cFiles.length === 0)} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:shadow-md active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none" style={{ background: "#806B61" }} data-testid="public-comment-send">
                      <MessageSquare className="h-4 w-4" /> {sending ? "Sender…" : "Send besked"}
                    </button>
                  </div>
                </div>
              </div>
              )}
            </Card>
          </Section>
        )}

        <footer className="mt-20 pt-8 border-t text-sm flex items-center gap-2" style={{ borderColor: "#EBE8E3", color: "#A8A29E" }}>
          <Building2 className="h-4 w-4" strokeWidth={1.5} /> BSM A/S · Projektpræsentation · {p.cust_share && !p.readonly ? "Kundevisning" : "Skrivebeskyttet visning"}
        </footer>
        </div>
      </div>
    </div>
  );
}
